"use client";

import { c as _c } from "react/compiler-runtime";
import { track } from "@vercel/analytics";
import { AnimatePresence } from "motion/react";
import { usePathname } from "next/navigation";
import { useActionState, useEffect, useState } from "react";
import { submitFeedback } from "@/app/(app)/feedback/actions";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import type { ActionResult } from "@/lib/action-result";
import { FeedbackForm } from "./feedback-form";
import { FeedbackSuccess } from "./feedback-success";
const MIN_LOADING_TIME = 600;
const SUCCESS_CLOSE_DELAY = 1500;
export function FeedbackPopover() {
  const $ = _c(9);
  const [openedAt, setOpenedAt] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = open => setOpenedAt(open ? Date.now() : null);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const t1 = openedAt !== null;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PopoverTrigger className="active:scale-100" render={<Button size="sm" variant="secondary" />}>Feedback</PopoverTrigger>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== openedAt) {
    t3 = openedAt !== null && <FeedbackPanel formLoadedAt={openedAt} key={openedAt} onDone={() => setOpenedAt(null)} />;
    $[2] = openedAt;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = <PopoverContent align="end" className="relative h-50 w-92 origin-top-right overflow-hidden rounded-2xl p-2">{t3}</PopoverContent>;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t1 || $[7] !== t4) {
    t5 = <Popover onOpenChange={t0} open={t1}>{t2}{t4}</Popover>;
    $[6] = t1;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function FeedbackPanel({
  formLoadedAt,
  onDone
}: {
  formLoadedAt: number;
  onDone: () => void;
}) {
  const pathname = usePathname();
  const [result, formAction, pending] = useActionState(async (_previous: ActionResult | null, formData: FormData) => {
    const started = Date.now();
    let outcome: ActionResult;
    try {
      outcome = await submitFeedback({
        formLoadedAt,
        message: String(formData.get("message") ?? ""),
        pageUrl: pathname,
        website: String(formData.get("website") ?? "")
      });
    } catch {
      outcome = {
        error: "Could not send feedback. Check your connection and try again.",
        ok: false
      };
    }
    if (outcome.ok) {
      track("Feedback Sent");
    }
    const remaining = MIN_LOADING_TIME - (Date.now() - started);
    if (remaining > 0) {
      await new Promise(resolve => {
        setTimeout(resolve, remaining);
      });
    }
    return outcome;
  }, null);
  useEffect(() => {
    if (result?.ok) {
      const timeout = setTimeout(onDone, SUCCESS_CLOSE_DELAY);
      return () => clearTimeout(timeout);
    }
  }, [result, onDone]);
  return <AnimatePresence initial={false} mode="popLayout">
      {result?.ok ? <FeedbackSuccess /> : <FeedbackForm action={formAction} error={result && !result.ok ? result.error : null} pending={pending} />}
    </AnimatePresence>;
}